/**
 * Composite Analyzer: Confluence Composite Intelligence (setup scope)
 *
 * Goes beyond the existing per-confluence WR-with/without analysis (handled
 * by setupConfluenceFit). Asks the questions a one-confluence-at-a-time
 * analyzer can't answer:
 *
 *   1. Which PAIRS of confluences are the trader's "winning recipe"?
 *      A single confluence may show no edge; the PAIR may carry the win
 *      rate. e.g., "Support" alone is meh; "Support + Volume" wins 80%.
 *      The pair is the real signal — surface it.
 *
 *   2. What does it cost to override a counterConfluence?
 *      counterConfluences are the trader's "warning signal" tags they set
 *      themselves. If they enter DESPITE a counter-confluence and it
 *      consistently goes red, the discipline gap is quantifiable.
 *      Right now no analyzer reads counterConfluences at all.
 *
 *   3. Which confluences are RESERVED — present in winners and absent in
 *      losers? Different from "WR-with" because it requires winners to
 *      share a confluence the losers DON'T have. The signature of a real
 *      filter, not a coincidence.
 *
 * Why "composite" — confluence pairs and winner/loser asymmetries can't be
 * detected one confluence at a time. This analyzer mines combinations.
 *
 * Reads:
 *   trade.confluences        — array of confluence IDs (the ones the trader saw FOR the trade)
 *   trade.counterConfluences — array of confluence IDs (warnings against the trade)
 *   trade.netPnl             — for win/loss bucketing
 *   ctx.allConfluences       — confluence library, for label resolution
 *
 * Trades without confluences arrays are silently skipped — the analyzer
 * just operates on the subset that has them.
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../../types.js';

export const key = 'confluenceComposite';
export const tier = 'free';

// Pair-mining thresholds. We need both pair members to actually appear
// together enough times to call it a recipe rather than a coincidence.
const PAIR_MIN_WINNERS         = 3;
const PAIR_MAX_LOSERS          = 1;
const PAIR_MIN_WR              = 0.70;     // The pair must actually win
// Override-cost thresholds. We need enough overrides to trust the loss
// signal, and the loss has to be meaningful relative to "no counter."
const OVERRIDE_MIN_TRADES      = 4;
const OVERRIDE_MIN_WR_GAP      = 0.20;
// Reserved-confluence: present in winners, absent in losers.
const RESERVED_MIN_WINNERS     = 4;
const RESERVED_MAX_LOSERS_WITH = 0;

const fmtPct = (n) => `${Math.round(n * 100)}%`;

/**
 * Build an id → label map from the confluences library so the user reads
 * "Support, Volume Spike" instead of opaque IDs in the observation text.
 */
const buildLabelMap = (allConfluences) => {
  const m = new Map();
  if (!Array.isArray(allConfluences)) return m;
  for (const c of allConfluences) {
    if (c?.id) m.set(c.id, c.label || c.name || c.id);
  }
  return m;
};

const labelFor = (id, labelMap) => labelMap.get(id) || id;

const labelList = (ids, labelMap, max = 4) => {
  if (!Array.isArray(ids) || ids.length === 0) return '';
  const labeled = ids.map(id => labelFor(id, labelMap));
  if (labeled.length <= max) return labeled.join(' + ');
  return labeled.slice(0, max).join(' + ') + ` +${labeled.length - max} more`;
};

const isWin = (t) => Number.isFinite(t.netPnl) && t.netPnl > 0;

/**
 * For a trade, return a sorted, deduped list of confluence IDs (so pair
 * keys are canonical regardless of input order).
 */
const confluencesOf = (trade) => {
  const arr = Array.isArray(trade?.confluences) ? trade.confluences : [];
  return [...new Set(arr.filter(Boolean))].sort();
};

const counterConfluencesOf = (trade) => {
  const arr = Array.isArray(trade?.counterConfluences) ? trade.counterConfluences : [];
  return [...new Set(arr.filter(Boolean))].sort();
};

// ── Pattern 1: Winning confluence pairs ──────────────────────────────────
//
// Mine all 2-element combinations of confluence IDs. For each pair, count
// how often it co-occurs in winners vs losers. Surface pairs that show up
// in winners much more than losers.

const detectWinningPairs = (setupName, trades, labelMap) => {
  const pairCount = new Map(); // "id1|id2" → { winners, losers, key: [id1, id2] }
  for (const t of trades) {
    const conf = confluencesOf(t);
    if (conf.length < 2) continue;
    const win = isWin(t);
    for (let i = 0; i < conf.length - 1; i++) {
      for (let j = i + 1; j < conf.length; j++) {
        const k = `${conf[i]}|${conf[j]}`;
        if (!pairCount.has(k)) pairCount.set(k, { winners: 0, losers: 0, key: [conf[i], conf[j]] });
        const e = pairCount.get(k);
        if (win) e.winners++; else e.losers++;
      }
    }
  }
  if (pairCount.size === 0) return null;

  // Filter to pairs that meet the "real recipe" thresholds.
  const candidates = [];
  for (const e of pairCount.values()) {
    const total = e.winners + e.losers;
    if (e.winners < PAIR_MIN_WINNERS) continue;
    if (e.losers > PAIR_MAX_LOSERS) continue;
    const wr = total > 0 ? e.winners / total : 0;
    if (wr < PAIR_MIN_WR) continue;
    candidates.push({ ...e, total, wr });
  }
  if (candidates.length === 0) return null;

  // Pick the strongest pair: highest WR, ties broken by total sample count.
  candidates.sort((a, b) => (b.wr - a.wr) || (b.total - a.total));
  const best = candidates[0];
  const labels = best.key.map(id => labelFor(id, labelMap));

  // Pin-the-pair action when ACTION_TYPE supports setup-scope pinning.
  const pinAction = makeAction({
    type: ACTION_TYPE.PIN_CONFLUENCE_TO_SETUP,
    label: 'Pin this pair to setup',
    payload: {
      setupName,
      confluenceIds: best.key,
      confluenceLabels: labels,
    },
    doneLabel: 'Pinned',
  });

  return makeObservation({
    id: `${key}:${setupName}:winning-pair:${best.key.join('-')}`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.CONFLUENCE,
    title: `"${setupName}" winning recipe: ${labels.join(' + ')} (${fmtPct(best.wr)} WR)`,
    detail: `${best.winners}/${best.total} winners when these two confluences appeared together. Either alone wasn't this strong — the pair is the signal. Sample is small but clean (${best.losers} losers).`,
    action: `Pin both confluences as required for this setup. Solo entries on either one alone are a different setup.`,
    actions: [pinAction],
    evidence: {
      pair: best.key,
      labels,
      winners: best.winners,
      losers: best.losers,
      wr: best.wr,
      candidatePairsConsidered: pairCount.size,
    },
    confidence: 0.78,
    priority: 82,
  });
};

// ── Pattern 2: Counter-confluence override cost ──────────────────────────
//
// counterConfluences = the trader's own "I see a warning signal" tags. If
// they entered ANYWAY and it bleeds, that's a discipline insight only this
// data can give — no other analyzer reads counterConfluences.

const detectOverrideCost = (setupName, trades, labelMap) => {
  const overrides = [];
  const clean = [];
  for (const t of trades) {
    const counters = counterConfluencesOf(t);
    if (counters.length > 0) overrides.push(t);
    else clean.push(t);
  }
  if (overrides.length < OVERRIDE_MIN_TRADES) return null;
  if (clean.length < OVERRIDE_MIN_TRADES) return null;

  const wr = (group) => group.filter(isWin).length / group.length;
  const overrideWr = wr(overrides);
  const cleanWr = wr(clean);
  const gap = cleanWr - overrideWr;
  if (gap < OVERRIDE_MIN_WR_GAP) return null;

  // The most common counter-confluence the trader was overriding —
  // names the specific warning being ignored.
  const counterFreq = new Map();
  for (const t of overrides) {
    for (const cid of counterConfluencesOf(t)) {
      counterFreq.set(cid, (counterFreq.get(cid) || 0) + 1);
    }
  }
  const sortedCounters = [...counterFreq.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([id, n]) => `${labelFor(id, labelMap)} (${n}×)`);

  // Total dollar bleed from overrides — concrete cost, not just a WR stat.
  const overrideNet = overrides.reduce((s, t) => s + (Number.isFinite(t.netPnl) ? t.netPnl : 0), 0);

  return makeObservation({
    id: `${key}:${setupName}:override-cost`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.DISCIPLINE,
    title: `"${setupName}" — overriding warnings cost ${fmtPct(gap)} of WR`,
    detail: `${overrides.length} trades taken DESPITE counter-confluences: ${fmtPct(overrideWr)} WR, net ${overrideNet >= 0 ? '+' : ''}$${Math.round(overrideNet).toLocaleString()}. Same setup with no counter flagged: ${fmtPct(cleanWr)} WR. Most-overridden warnings: ${sortedCounters.join(', ')}.`,
    action: `Treat counter-confluences as a hard pass. Your own data says they're real — the override is the leak.`,
    evidence: {
      overrideCount: overrides.length,
      cleanCount: clean.length,
      overrideWr,
      cleanWr,
      gap,
      overrideNet,
      topCounters: [...counterFreq.entries()].slice(0, 5),
    },
    confidence: 0.82,
    priority: 84,
  });
};

// ── Pattern 3: Reserved confluences (winners only) ───────────────────────
//
// Per-confluence: present in winners, completely absent in losers. This is
// the strict version of "WR-with" — the confluence isn't just helpful, it's
// gating. Different signal from setupConfluenceFit which uses a 20pp WR-gap
// threshold; here we look for confluences that NEVER appear when you lose.

const detectReservedConfluences = (setupName, trades, labelMap) => {
  const winners = trades.filter(isWin);
  const losers  = trades.filter(t => Number.isFinite(t.netPnl) && t.netPnl <= 0);
  if (winners.length < RESERVED_MIN_WINNERS) return null;
  if (losers.length < 2) return null;  // Can't meaningfully say "absent in losers" with no losers

  const winFreq = new Map();
  for (const t of winners) {
    for (const cid of confluencesOf(t)) {
      winFreq.set(cid, (winFreq.get(cid) || 0) + 1);
    }
  }
  const loseSet = new Set();
  for (const t of losers) {
    for (const cid of confluencesOf(t)) loseSet.add(cid);
  }

  // Reserved = appears in ≥RESERVED_MIN_WINNERS winners AND in ≤RESERVED_MAX_LOSERS_WITH losers.
  const reserved = [];
  for (const [cid, count] of winFreq) {
    if (count < RESERVED_MIN_WINNERS) continue;
    if (loseSet.has(cid) && RESERVED_MAX_LOSERS_WITH === 0) continue;
    reserved.push({ id: cid, winnerCount: count, label: labelFor(cid, labelMap) });
  }
  if (reserved.length === 0) return null;

  reserved.sort((a, b) => b.winnerCount - a.winnerCount);
  const top = reserved.slice(0, 3);
  const labels = top.map(r => `${r.label} (${r.winnerCount}/${winners.length} winners)`);

  return makeObservation({
    id: `${key}:${setupName}:reserved-${top.map(r => r.id).join('-')}`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.CONFLUENCE,
    title: `"${setupName}" — ${top.length === 1 ? 'a confluence is' : 'confluences are'} reserved for winners only`,
    detail: `${labels.join(', ')}. ${top.length === 1 ? 'This confluence appears' : 'These confluences each appear'} in your winners but never in losers — strict gating signal, not just a "helpful" tag.`,
    action: top.length === 1
      ? `Make this confluence a required filter on this setup, not optional.`
      : `Treat these as required filters. The data says you don't take ${setupName} losers when they're present.`,
    evidence: {
      reserved: top,
      winnerCount: winners.length,
      loserCount: losers.length,
    },
    confidence: 0.8,
    priority: 80,
  });
};

// ── Public analyze() ─────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { setupName, setupTrades, allConfluences = [] } = ctx || {};
  if (!setupName || !Array.isArray(setupTrades) || setupTrades.length === 0) return null;

  // Live-trade gate: winning-pair mining, override-cost gap, and
  // reserved-confluence detection all bucket trades by winner vs loser.
  // Open trades have unrealized P&L which isn't a realized outcome —
  // including them would fire premature "winning recipe" / "override is
  // bleeding" claims while the user is still in the trade (panic-exit
  // risk). Matches summarizePnl semantics.
  const closedSetupTrades = setupTrades.filter(t => t?.isOpen !== true);
  if (closedSetupTrades.length === 0) return null;

  const labelMap = buildLabelMap(allConfluences);
  const out = [];

  const pair = detectWinningPairs(setupName, closedSetupTrades, labelMap);
  if (pair) out.push(pair);

  const override = detectOverrideCost(setupName, closedSetupTrades, labelMap);
  if (override) out.push(override);

  const reserved = detectReservedConfluences(setupName, closedSetupTrades, labelMap);
  if (reserved) out.push(reserved);

  return out.length === 0 ? null : out;
};

// Exports for tests
export {
  buildLabelMap,
  confluencesOf,
  counterConfluencesOf,
  detectWinningPairs,
  detectOverrideCost,
  detectReservedConfluences,
  PAIR_MIN_WINNERS,
  PAIR_MAX_LOSERS,
  PAIR_MIN_WR,
  OVERRIDE_MIN_TRADES,
  OVERRIDE_MIN_WR_GAP,
  RESERVED_MIN_WINNERS,
};
